import api from '@/api';
import { ref, onMounted, watch, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

export function useToolIndex() {
  const { locale } = useI18n();
  const router = useRouter();
  
  // Estado reativo
  const rawToolsData = ref([]);
  const loading = ref(false);
  const expandedItems = ref(new Set());
  const lastClickTime = ref(0);
  const isNavigating = ref(false);

  // Cache para evitar reprocessamento
  const toolsCache = ref(new Map());
  const currentLanguage = ref(getCurrentLanguage());

  function getCurrentLanguage() {
    return localStorage.getItem('selectedLanguage') || locale.value || 'pt';
  }

  // Computed otimizado para organizar as ferramentas
  const allTools = computed(() => {
    const cacheKey = `${currentLanguage.value}_${rawToolsData.value.length}`;
    
    if (toolsCache.value.has(cacheKey)) {
      return toolsCache.value.get(cacheKey);
    }

    const tools = rawToolsData.value;
    if (!tools.length) return [];

    // Agrupar por nível uma única vez
    const toolsByLevel = {
      0: new Map(),
      1: new Map(),
      2: new Map()
    };

    tools.forEach(tool => {
      if (tool.nivel >= 0 && tool.nivel <= 2) {
        toolsByLevel[tool.nivel].set(tool.id, {
          ...tool,
          displayText: `${tool.nome} - ${tool.descricao}`
        });
      }
    });

    // Construir hierarquia otimizada
    const organizedTools = Array.from(toolsByLevel[0].values())
      .sort((a, b) => a.nome.localeCompare(b.nome))
      .map(item0 => {
        const subferramentas1 = Array.from(toolsByLevel[1].values())
          .filter(item1 => item1.ferramenta_pai_id === item0.id)
          .sort((a, b) => a.nome.localeCompare(b.nome))
          .map(item1 => {
            const subferramentas2 = Array.from(toolsByLevel[2].values())
              .filter(item2 => item2.ferramenta_pai_id === item1.id)
              .sort((a, b) => a.nome.localeCompare(b.nome));

            return {
              ...item1,
              subferramentas: subferramentas2,
            };
          });

        return {
          ...item0,
          subferramentas: subferramentas1,
        };
      });

    // Cachear resultado
    toolsCache.value.set(cacheKey, organizedTools);
    return organizedTools;
  });

  const fetchAllTools = async (force = false) => {
    const newLang = getCurrentLanguage();
    
    // Evitar fetch desnecessário se idioma não mudou
    if (!force && currentLanguage.value === newLang && rawToolsData.value.length > 0) {
      return;
    }

    if (loading.value) return;

    try {
      loading.value = true;
      currentLanguage.value = newLang;
      
      const response = await api.searchTools({ idioma: newLang });
      rawToolsData.value = response.data || [];
      
      // Limpar cache quando dados mudam
      toolsCache.value.clear();

    } catch (error) {
      console.error('Erro ao buscar todas as ferramentas:', error);
      rawToolsData.value = [];
    } finally {
      loading.value = false;
    }
  };

  // Toggle otimizado sem forçar reatividade
  const toggleExpand = (id) => {
    if (expandedItems.value.has(id)) {
      expandedItems.value.delete(id);
    } else {
      expandedItems.value.add(id);
    }
    // Trigger reatividade apenas quando necessário
    expandedItems.value = new Set(expandedItems.value);
  };

  // Debounce para cliques rápidos
  const handleMenuItemClick = async (ferramenta) => {
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTime.value;
    
    // Prevenir cliques muito rápidos (menos de 300ms)
    if (timeSinceLastClick < 300 || isNavigating.value) {
      return;
    }
    
    lastClickTime.value = now;

    if (!ferramenta || typeof ferramenta.nivel === 'undefined') {
      console.error('Ferramenta inválida ou nível não definido:', ferramenta);
      return;
    }

    const routeMap = {
      0: 'LevelZero',
      1: 'LevelOne',
      2: 'LevelTwo'
    };

    const routeName = routeMap[ferramenta.nivel];
    if (!routeName) {
      console.error('Nível de ferramenta desconhecido:', ferramenta.nivel);
      return;
    }

    try {
      isNavigating.value = true;
      
      // Usar nextTick para garantir que a UI seja atualizada antes da navegação
      await nextTick();
      
      await router.push({ 
        name: routeName, 
        params: { id: ferramenta.id } 
      });
      
      // Scroll assíncrono para não bloquear a thread
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
      
    } catch (error) {
      console.error('Erro na navegação:', error);
    } finally {
      // Reset após um delay para evitar problemas
      setTimeout(() => {
        isNavigating.value = false;
      }, 500);
    }
  };

  onMounted(() => {
    fetchAllTools(true);
  });

  // Watch otimizado - só executa quando idioma realmente muda
  watch(() => getCurrentLanguage(), (newLang) => {
    if (newLang !== currentLanguage.value) {
      fetchAllTools(true);
    }
  });

  return {
    allTools,
    loading,
    expandedItems,
    toggleExpand,
    handleMenuItemClick,
  };
}